import { LONG_CAST } from '../state/spells.js';
import { AREA_LIMITS } from '../state/areas.js';
export const METAMAGIC_LIMITS = { keys: 9 };
const STANDARD = 'standard';
/** A wire's choice: exact shape or null. Untrusted input. */
export function parseMetamagicAsk(v, raise) {
    if (!Array.isArray(v) || !v.length || v.length > METAMAGIC_LIMITS.keys || !v.every((k) => typeof k === 'string' && /^[a-z0-9_-]{1,40}$/.test(k)))
        return null;
    if (new Set(v).size !== v.length)
        return null;
    if (raise !== undefined && !(Number.isInteger(raise) && raise >= 1 && raise <= 99))
        return null;
    return { keys: [...v], ...(raise !== undefined ? { raise: raise } : {}) };
}
/** The choices a sheet's feats give, in the system's order. */
export function knownMetamagic(rules, feats) {
    if (!rules || !feats?.length)
        return [];
    const have = new Set(feats.map((f) => f.trim().toLowerCase()));
    return rules.feats.filter((c) => have.has(c.feat.toLowerCase()));
}
/** Why this choice cannot go on this spell by this caster, in the system's words; null when it can. */
export function metamagicRefusal(c, spell, caster) {
    if (c.refusedFor && caster.casting && c.refusedFor.casting.includes(caster.casting.split('/').pop()))
        return c.refusedFor.reason;
    if (c.spontaneous === 'refused' && caster.spontaneous)
        return c.spontaneousRefused ?? null;
    if (c.noLong && spell.castingTime === LONG_CAST)
        return c.noLong;
    if (c.area && (spell.noArea || !c.area.shapes.includes(spell.shape)))
        return c.areaRefused ?? null;
    return null;
}
/** The slot a choice set needs: the level raised to, then each choice's levels on top. */
const slotOf = (level, chosen, raise) => chosen.reduce((n, c) => n + (c.slots === 'choose' ? 0 : c.slots), chosen.some((c) => c.slots === 'choose') ? raise ?? level : level);
/** A choice set on a spell: what the cast is, or the words of why it cannot be. Pure: the bar's preview and the server's check. */
export function applyMetamagic(rules, known, spell, caster, ask) {
    const chosen = [];
    for (const key of ask.keys) {
        const c = known.find((k) => k.key === key);
        if (!c)
            return { refused: `${spell.name}: that choice is not one of this character's feats.` };
        const no = metamagicRefusal(c, spell, caster);
        if (no)
            return { refused: no };
        chosen.push(c);
    }
    const raising = chosen.find((c) => c.slots === 'choose');
    if (raising && (ask.raise === undefined || ask.raise <= caster.level || ask.raise > rules.highest))
        return { refused: `${raising.label}: name a level above ${caster.level}, at most ${rules.highest}.` };
    const slot = slotOf(caster.level, chosen, ask.raise);
    if (slot > caster.top || slot > rules.highest)
        return { refused: `${spell.name} would need a level ${slot} slot, and this character's highest is level ${caster.top}.` };
    const boost = {};
    for (const c of chosen) {
        if (c.boost?.maximum)
            boost.maximum = true;
        if (c.boost?.share)
            boost.share = c.boost.share;
    }
    const times = chosen.reduce((n, c) => n * (c.area?.times ?? 1), 1);
    const raised = raising ? ask.raise - caster.level : 0;
    const quick = chosen.find((c) => c.castingTime);
    const own = spell.castingTime ?? STANDARD;
    const spontaneous = caster.spontaneous && !chosen.every((c) => c.spontaneous === 'exempt') && !quick;
    const longer = spontaneous && own === rules.spontaneous.from;
    const castingTime = quick?.castingTime ?? (longer ? rules.spontaneous.to : own);
    const size = Math.min(AREA_LIMITS.squares, spell.size * times);
    return {
        level: caster.level, slot, name: `${spell.name} (${chosen.map((c) => c.word).join(', ')})`.slice(0, 60), castingTime,
        dc: spell.dc === null ? null : spell.dc + raised * (raising?.perLevel ?? 0), size,
        ...(spell.size2 !== undefined ? { size2: Math.min(AREA_LIMITS.squares, spell.size2 * times) } : {}),
        ...(Object.keys(boost).length ? { boost } : {}),
        notes: [...chosen.flatMap((c) => [...(c.note ? [c.note] : []), ...(c.diceNote && !spell.damage ? [c.diceNote] : [])]), ...(spontaneous && !longer ? [rules.spontaneous.longer] : [])],
    };
}
/** The cast line, the same for the DM and the players; `action` the casting time's words. */
export function metamagicLine(who, m, action) {
    return `${who} casts ${m.name}: a level ${m.level} spell in a level ${m.slot} slot${action ? `, as a ${action.toLowerCase()}` : ''}.${m.notes.length ? ` ${m.notes.join(' ')}` : ''}`;
}
//# sourceMappingURL=metamagic.js.map